import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  assessmentsService,
  FinalReportAiAssist,
  FinalReportAiEditResult,
  FinalReportAreaEntry,
} from '../api/assessmentsService';
import { saveStateLabel, useDebouncedAutosave } from '../hooks/useDebouncedAutosave';
import AutoGrowTextarea from './AutoGrowTextarea';
import { AiEditReview, AiEditTarget, AiToolMenu } from './FinalReportAiEdit';

type Rating = 'red' | 'yellow' | 'green';

const ratingDotClass: Record<Rating, string> = {
  red: 'bg-red-500',
  yellow: 'bg-yellow-400',
  green: 'bg-green-500',
};

const statusChipClass = (status: FinalReportAreaEntry['reportStatus']) => {
  if (status === 'submitted' || status === 'approved' || status === 'reviewed') return 'bg-green-100 text-green-800';
  if (status === 'draft' || status === 'returned') return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-500';
};

const statusLabel = (status: FinalReportAreaEntry['reportStatus']) => {
  if (!status) return 'Not started';
  if (status === 'returned') return 'Returned';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

/**
 * One interview area on the report writer's page.
 *
 * Shows the assessor's own report read-only alongside an editable copy of the
 * wording that will be delivered. Editing here never touches the assessor's
 * report; it saves a final report section instead.
 */
interface AiReviewState {
  /** The full area text at request time; the diff is against this. */
  original: string;
  modeLabel: string;
  /** `edited` is always the full text here, even when only a selection was sent. */
  result: FinalReportAiEditResult;
}

interface AiEditVariables {
  /** What is sent to the API: the whole text or just the highlighted passage. */
  text: string;
  mode: string;
  scope: 'full' | 'selection';
  /** Full area text at request time, for the review diff. */
  fullText: string;
  /** Where the passage sits in `fullText` (selection scope only). */
  range?: { start: number; end: number };
}

interface TextSelection {
  start: number;
  end: number;
}

const selectionOf = (el: HTMLTextAreaElement | null): TextSelection | null => {
  if (!el) return null;
  const { selectionStart, selectionEnd, value } = el;
  if (selectionEnd <= selectionStart) return null;
  if (!value.slice(selectionStart, selectionEnd).trim()) return null;
  return { start: selectionStart, end: selectionEnd };
};

const FinalReportAreaCard: React.FC<{
  assessmentId: number;
  participantId: number;
  entry: FinalReportAreaEntry;
  canEdit: boolean;
  onSectionReset: () => void;
  aiAssist?: FinalReportAiAssist;
}> = ({ assessmentId, participantId, entry, canEdit, onSectionReset, aiAssist }) => {
  const assessorText = entry.reportNotes || '';
  const [content, setContent] = useState(entry.finalContent ?? assessorText);
  const [locallyEdited, setLocallyEdited] = useState(false);
  const [showAssessorText, setShowAssessorText] = useState(false);
  const [showInternal, setShowInternal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiReview, setAiReview] = useState<AiReviewState | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [selection, setSelection] = useState<TextSelection | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const hydrationKey = `${participantId}:${entry.roleId}`;
  const hydratedForRef = useRef<string | null>(null);

  // Hydrate once per candidate/area so a refetch triggered elsewhere on the
  // page cannot overwrite what the writer is typing.
  useEffect(() => {
    if (hydratedForRef.current === hydrationKey) return;
    setContent(entry.finalContent ?? assessorText);
    setLocallyEdited(false);
    setAiReview(null);
    setAiError(null);
    setSelection(null);
    hydratedForRef.current = hydrationKey;
  }, [hydrationKey, entry.finalContent, assessorText]);

  const saveMutation = useMutation({
    mutationFn: (value: string) =>
      assessmentsService.saveFinalReportSection(assessmentId, participantId, entry.roleId, value),
    onSuccess: () => {
      setLocallyEdited(true);
      setError(null);
    },
  });

  const { saveState, setSaveState, schedule, cancel } = useDebouncedAutosave<string>({
    enabled: canEdit,
    save: (value) => saveMutation.mutateAsync(value),
  });

  useEffect(() => cancel, [hydrationKey, cancel]);

  const resetMutation = useMutation({
    mutationFn: () => assessmentsService.resetFinalReportSection(assessmentId, participantId, entry.roleId),
    onSuccess: () => {
      setContent(assessorText);
      setLocallyEdited(false);
      setSaveState('idle');
      setError(null);
      onSectionReset();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || 'Could not reset this area.');
    },
  });

  const aiModes = aiAssist?.modes ?? [];
  const aiEnabled = Boolean(canEdit && aiAssist?.enabled && aiModes.length > 0);

  // Suggestion only: the API returns a revised copy and nothing is written
  // until the writer applies their accepted changes (a normal section save).
  const aiEditMutation = useMutation({
    mutationFn: ({ text, mode, scope }: AiEditVariables) =>
      assessmentsService.aiEditFinalReportText(assessmentId, participantId, { text, mode, scope }),
    onSuccess: (response, variables) => {
      const modeLabel = aiModes.find((mode) => mode.id === variables.mode)?.label || 'AI edit';
      const { fullText, range } = variables;

      // A selection edit comes back as just the passage; put it back in place
      // (keeping the whitespace the writer's highlight included) so the review
      // shows the whole area with changes only inside the highlighted part.
      let edited = response.data.edited;
      if (variables.scope === 'selection' && range) {
        const passage = fullText.slice(range.start, range.end);
        const lead = passage.match(/^\s*/)?.[0] ?? '';
        const trail = passage.match(/\s*$/)?.[0] ?? '';
        edited = fullText.slice(0, range.start) + lead + response.data.edited + trail + fullText.slice(range.end);
      }

      setAiReview({
        original: fullText,
        modeLabel,
        result: { ...response.data, edited },
      });
      setAiError(null);
    },
    onError: (err: any) => {
      setAiError(err?.response?.data?.message || 'The AI edit could not be completed. Try again.');
    },
  });

  const runAiEdit = (mode: string, target: AiEditTarget) => {
    if (!content.trim() || aiEditMutation.isPending) return;
    setAiError(null);

    // Read the live selection from the element rather than state so the
    // passage always matches what is highlighted at the moment of the click.
    const live = target === 'selection' ? (selectionOf(textareaRef.current) ?? selection) : null;
    if (live) {
      aiEditMutation.mutate({
        text: content.slice(live.start, live.end).trim(),
        mode,
        scope: 'selection',
        fullText: content,
        range: live,
      });
      return;
    }
    aiEditMutation.mutate({ text: content, mode, scope: 'full', fullText: content });
  };

  const applyAiEdit = (next: string) => {
    setAiReview(null);
    setSelection(null);
    if (next === content) return;
    setContent(next);
    schedule(next);
  };

  const selectionText = selection ? content.slice(selection.start, selection.end) : '';

  const isEdited = Boolean(entry.isEdited) || locallyEdited;
  const differsFromAssessor = content.trim() !== assessorText.trim();

  const handleReset = () => {
    if (!window.confirm(`Replace the final wording for ${entry.roleName} with the assessor's text?`)) return;
    cancel();
    setAiReview(null);
    resetMutation.mutate();
  };

  return (
    <div className="rounded-lg border border-gray-200">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">{entry.roleName}</span>
          {entry.initialAreaRating && (
            <span className={['inline-flex h-2.5 w-2.5 rounded-full', ratingDotClass[entry.initialAreaRating]].join(' ')} />
          )}
          <span className={['rounded-full px-2 py-0.5 text-[11px] font-medium', statusChipClass(entry.reportStatus)].join(' ')}>
            {statusLabel(entry.reportStatus)}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {entry.assessorName ? `Assessor: ${entry.assessorName}` : 'Assessor: —'}
          </span>
          {aiEnabled && (
            <AiToolMenu
              modes={aiModes}
              disabled={!content.trim() || aiReview !== null}
              busy={aiEditMutation.isPending}
              selectionText={selectionText}
              onSelect={runAiEdit}
            />
          )}
        </div>
      </div>

      <div className="space-y-3 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={[
                'rounded-full px-2 py-0.5 text-[11px] font-medium',
                isEdited || differsFromAssessor ? 'bg-primary-50 text-primary-700' : 'bg-gray-100 text-gray-600',
              ].join(' ')}
            >
              {isEdited || differsFromAssessor ? 'Edited for delivery' : 'Using assessor text'}
            </span>
            {entry.isStale && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                Assessor revised their report since your edit
              </span>
            )}
          </div>
          {canEdit && (
            <span className={['text-xs', saveState === 'error' ? 'text-red-700' : 'text-gray-500'].join(' ')}>
              {saveState === 'error' ? 'Could not save' : saveStateLabel[saveState]}
            </span>
          )}
        </div>

        {aiReview ? (
          <AiEditReview
            original={aiReview.original}
            result={aiReview.result}
            modeLabel={aiReview.modeLabel}
            onApply={applyAiEdit}
            onCancel={() => setAiReview(null)}
          />
        ) : (
          <AutoGrowTextarea
            textareaRef={textareaRef}
            value={content}
            onChange={(value) => {
              setContent(value);
              schedule(value);
              setSelection(selectionOf(textareaRef.current));
            }}
            onSelect={(event) => setSelection(selectionOf(event.currentTarget))}
            disabled={!canEdit || aiEditMutation.isPending}
            minHeight={140}
            placeholder={assessorText ? undefined : 'No assessor report yet.'}
          />
        )}

        {aiEditMutation.isPending && (
          <p className="flex items-center gap-2 text-xs text-gray-600">
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600" />
            Working on {aiModes.find((mode) => mode.id === aiEditMutation.variables?.mode)?.label.toLowerCase() || 'the edit'}
            {aiEditMutation.variables?.scope === 'selection' ? ' for your selection' : ''}…
          </p>
        )}

        {aiError && (
          <p className="text-xs text-red-700">
            {aiError}
            {aiEditMutation.variables && (
              <button
                type="button"
                className="ml-2 font-medium underline underline-offset-2"
                onClick={() => aiEditMutation.mutate(aiEditMutation.variables as AiEditVariables)}
              >
                Try again
              </button>
            )}
          </p>
        )}

        {error && <p className="text-xs text-red-700">{error}</p>}

        <div className="flex flex-wrap items-center gap-3 text-xs">
          <button
            type="button"
            className="font-medium text-primary-600 hover:text-primary-700"
            onClick={() => setShowAssessorText((prev) => !prev)}
          >
            {showAssessorText ? "Hide assessor's report" : "Show assessor's report"}
          </button>
          {entry.afterPartNotes?.trim() && (
            <button
              type="button"
              className="font-medium text-primary-600 hover:text-primary-700"
              onClick={() => setShowInternal((prev) => !prev)}
            >
              {showInternal ? 'Hide after-part notes' : 'Show after-part notes (team only)'}
            </button>
          )}
          {canEdit && isEdited && (
            <button
              type="button"
              className="text-gray-500 underline underline-offset-2 hover:text-gray-700 disabled:opacity-60"
              onClick={handleReset}
              disabled={resetMutation.isPending}
            >
              Reset to assessor text
            </button>
          )}
        </div>

        {showAssessorText && (
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              As written by the assessor
            </div>
            <div className="whitespace-pre-wrap text-sm text-gray-800">
              {assessorText || 'No report submitted yet.'}
            </div>
          </div>
        )}

        {showInternal && entry.afterPartNotes?.trim() && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
              After-part notes — never delivered
            </div>
            <div className="whitespace-pre-wrap text-sm text-gray-800">{entry.afterPartNotes}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinalReportAreaCard;
